import { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useAuthStore } from "./store/authStore";
import { SearchProvider } from "./components/SearchProvider";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

// Layouts
import MainLayout from "./layouts/MainLayout";

// Pages
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import NotFoundPage from "./pages/NotFoundPage";
import ProfilePage from "./pages/ProfilePage";
import ReviewDetailPage from "./pages/ReviewDetailPage";

// Features
import SchoolList from "./features/schools/SchoolList";
import SchoolDetail from "./features/schools/SchoolDetail";
import SchoolForm from "./features/schools/SchoolForm";
import ReviewForm from "./features/reviews/ReviewForm";
import AdminDashboard from "./features/dashboard/AdminDashboard";
import ReviewListPage from "./pages/ReviewListPage";

// Protected Route Component
function ProtectedRoute({ children, adminOnly = false }) {
  const { isAuthenticated, isAdmin } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Public Route Component (redirect if already authenticated)
function PublicRoute({ children }) {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  const { initAuth } = useAuthStore();

  useEffect(() => {
    // Initialize auth state on app start
    initAuth();
  }, [initAuth]);

  return (
    <Router>
      <SearchProvider>
        <MainLayout>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/schools" element={<SchoolList />} />
            <Route path="/schools/:id" element={<SchoolDetail />} />
            <Route path="/reviews" element={<ReviewListPage />} />
            <Route path="/reviews/:id" element={<ReviewDetailPage />} />

            {/* Auth routes - redirect if already logged in */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              }
            />

            {/* Protected routes - require authentication */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/schools/create"
              element={
                <ProtectedRoute>
                  <SchoolForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reviews/create/:schoolId"
              element={
                <ProtectedRoute>
                  <ReviewForm />
                </ProtectedRoute>
              }
            />

            {/* Admin only routes */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </MainLayout>
      </SearchProvider>
      <Analytics />
      <SpeedInsights />
    </Router>
  );
}

export default App;
